import { css, cva, cx } from '@/styled-system/css';

/**
 * Root container for the carousel
 */
export const carouselRoot = css({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
});

/**
 * Adds a gradient fade on the right edge to indicate scrollable overflow.
 * Applied to the carousel root alongside carouselRoot.
 */
export const carouselFadeEdge = css({
  _after: {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '40px',
    backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1))',
    pointerEvents: 'none',
    zIndex: 'zIndexStep',
  },
});

/**
 * Base styles for the carousel track (scrolling <ul> container)
 */
export const carouselTrack = css({
  display: 'flex',
  flexDirection: 'row',
  listStyle: 'none',
  margin: 0,
  padding: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  boxSizing: 'border-box',

  // Hide scrollbar on WebKit browsers (Chrome, Safari, Edge)
  '&::-webkit-scrollbar': {
    display: 'none',
  },

  // Focus management for keyboard navigation
  _focusVisible: {
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: 'hpxTeal500',
    outlineOffset: '2px',
  },
});

/**
 * Extra right padding for the track when fadeEdge is enabled,
 * so the last item isn't hidden under the gradient overlay.
 */
export const carouselTrackFadePadding = css({
  paddingRight: '5x',
});

/**
 * CVA variants for the carousel track
 */
export const carouselTrackVariants = cva({
  variants: {
    gap: {
      xs: { gap: 'xs' }, // 4px
      sm: { gap: '1x' }, // 8px
      md: { gap: '2x' }, // 16px
      lg: { gap: '3x' }, // 24px
    },
    snapAlign: {
      start: { scrollSnapType: 'x mandatory' },
      center: { scrollSnapType: 'x mandatory' },
      end: { scrollSnapType: 'x mandatory' },
      none: { scrollSnapType: 'none' },
    },
    scrollBehavior: {
      auto: { scrollBehavior: 'auto' },
      smooth: { scrollBehavior: 'smooth' },
    },
  },
  compoundVariants: [
    {
      snapAlign: 'start',
      css: { '& > li': { scrollSnapAlign: 'start' } },
    },
    {
      snapAlign: 'center',
      css: { '& > li': { scrollSnapAlign: 'center' } },
    },
    {
      snapAlign: 'end',
      css: { '& > li': { scrollSnapAlign: 'end' } },
    },
  ],
  defaultVariants: {
    gap: 'md',
    snapAlign: 'start',
    scrollBehavior: 'smooth',
  },
});

/**
 * Base styles for carousel items (<li> elements)
 */
export const carouselItem = css({
  flexShrink: 0,
  listStyle: 'none',
  minWidth: 0,
  boxSizing: 'border-box',
});

/**
 * Header container styles
 */
export const carouselHeader = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '2x',
});

/**
 * Title styles following HALO typography patterns
 */
export const carouselTitle = css({
  margin: 0,
  fontWeight: 'bold',
  color: 'hpxBlue600',
});

/**
 * CVA variants for title heading levels
 */
export const carouselTitleVariants = cva({
  variants: {
    level: {
      1: { fontSize: 'hero' }, // 40px
      2: { fontSize: 'xl' }, // 24px
      3: { fontSize: 'lg' }, // 20px
      4: { fontSize: 'md' }, // 16px
      5: { fontSize: 'sm' }, // 14px
      6: { fontSize: 'tiny' }, // 12px
    },
  },
  defaultVariants: {
    level: 3,
  },
});

/**
 * Controls container styles
 */
export const carouselControls = css({
  display: 'flex',
  alignItems: 'center',
  gap: '1x',
});

/**
 * Navigation button styles (extends existing Button component)
 */
export const carouselControlButton = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2x',
  height: '2x',
  borderRadius: '4px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'hpxGrey400',
  background: 'hpxWhite',
  cursor: 'pointer',
  transitionProperty: 'all',
  transitionDuration: '0.2s',
  transitionTimingFunction: 'ease',

  '&:hover': {
    background: 'hpxGrey100',
    borderColor: 'hpxGrey500',
  },

  '&:focus-visible': {
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: 'hpxTeal500',
    outlineOffset: '2px',
  },

  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
});

/**
 * Skip links container (visually hidden until focused)
 */
export const carouselSkipLinks = css({
  display: 'flex',
  gap: '1x',
  marginBottom: '1x',
});

/**
 * Skip link styles (accessible but visually hidden until focused)
 */
export const carouselSkipLink = css({
  position: 'absolute',
  left: '-9999px',
  width: '1px',
  height: '1px',
  overflow: 'hidden',

  '&:focus-visible': {
    position: 'static',
    left: 'auto',
    width: 'auto',
    height: 'auto',
    overflow: 'visible',
    paddingTop: '1x',
    paddingRight: '2x',
    paddingBottom: '1x',
    paddingLeft: '2x',
    background: 'hpxTeal500',
    color: 'hpxWhite',
    textDecoration: 'none',
    borderRadius: '4px',
  },
});

/**
 * Indicators (dot pagination)
 */
export const indicatorsContainer = css({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: '1x',
});

export const dotBase = css({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  border: 'none',
  padding: '0',
  cursor: 'pointer',
  background: 'transparent',
  position: 'relative',
  _before: {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    transitionProperty: 'background-color',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
  },
});

export const dotInactive = css({
  _before: { bg: 'hpxGrey400' },
});

export const dotActive = css({
  _before: { bg: 'hpxTeal500' },
});

export const dotInactiveLight = css({
  _before: { bg: 'rgba(255, 255, 255, 0.5)' },
});

export const dotActiveLight = css({
  _before: { bg: 'hpxWhite' },
});

/**
 * Helper function to combine classes properly
 */
export const combineCarouselClasses = (...classes: Array<string | undefined | false>) => {
  return cx(...classes.filter(Boolean));
};
